// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

import { Link } from "react-router-dom"
import { GuardRokIcon } from "../components/GuardRokLogo"

const LAST_UPDATED = "May 25, 2026 (v3.0)"
const CONTACT = "privacy@guardrok.com"

export default function Privacy() {
  return (
    <div style={{ minHeight: "100vh", background: "#030712", color: "#fff", fontFamily: "system-ui, sans-serif" }}>

      {/* Nav */}
      <nav style={{ padding: "20px 48px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #0f172a", position: "sticky", top: 0, background: "rgba(3,9,18,0.95)", backdropFilter: "blur(12px)", zIndex: 100 }}>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none" }}>
          <GuardRokIcon size={36} />
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, letterSpacing: "-0.5px", lineHeight: 1, color: "#fff" }}>Guard<span style={{ color: "#E63946" }}>Rok</span><span style={{ fontSize: 8, color: "#6b7280", verticalAlign: "super", marginLeft: 1 }}>™</span></div>
            <div style={{ fontSize: 9, letterSpacing: "3px", color: "#6b7280", textTransform: "uppercase" as const, marginTop: 2 }}>AI Firewall</div>
          </div>
        </Link>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <Link to="/docs"    style={{ color: "#9ca3af", fontSize: 14, textDecoration: "none" }}>Docs</Link>
          <Link to="/pricing" style={{ color: "#9ca3af", fontSize: 14, textDecoration: "none" }}>Pricing</Link>
          <Link to="/login"   style={{ color: "#9ca3af", fontSize: 14, textDecoration: "none" }}>Sign in</Link>
        </div>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "64px 40px 96px" }}>
        <div style={{ fontSize: 12, color: "#475569", marginBottom: 12 }}>Last updated: {LAST_UPDATED}</div>
        <h1 style={{ fontSize: 36, fontWeight: 900, letterSpacing: "-1px", marginBottom: 10 }}>Privacy Policy</h1>
        <p style={{ color: "#64748b", fontSize: 15, marginBottom: 48, lineHeight: 1.7 }}>
          GuardRok ("we", "us", "our") is operated by aiApas LLC. This policy explains what data we collect, why we collect it, and how we protect it.
        </p>

        {[
          {
            title: "1. What GuardRok does",
            body: [
              "GuardRok is a Chrome extension that monitors prompts typed into supported AI tools on behalf of your organization. When a prompt is submitted, GuardRok checks it against your organization's data policies in real time and takes the configured action — allowing, blocking, redacting, or warning — before the prompt reaches the AI tool.",
              "Prompt content is processed in memory only. The text of employee prompts is never stored.",
            ],
          },
          {
            title: "2. What we collect",
            body: [
              "Account information — name, work email, company name, and role provided at sign-up.",
              "Event metadata — when a policy check occurred, which AI tool was involved, what action was taken, and which category of policy was triggered (e.g. PHI, PII, financial data). The prompt text itself is never included.",
              "User identifier — an anonymous identifier used to attribute events to the correct employee within your organization's dashboard.",
              "Device and session information — browser type and session token for authentication purposes.",
              "Demo and contact form submissions — name, email, company, and message when you voluntarily contact us.",
            ],
          },
          {
            title: "3. What we never collect",
            body: [
              "Prompt text — the words employees type are never stored, whether the prompt was allowed or blocked.",
              "AI tool responses — we do not intercept or store any response from ChatGPT, Copilot, Gemini, Claude, or any other AI tool.",
              "We do not sell, rent, or share personal data with advertisers.",
              "We do not use any data to train AI models.",
              "We do not monitor personal devices. GuardRok operates only through the installed extension on organization-managed browsers.",
            ],
          },
          {
            title: "4. How we use your data",
            body: [
              "To provide the GuardRok service and enforce your organization's data policies.",
              "To generate compliance audit logs accessible to your organization's administrators.",
              "To send transactional communications such as account confirmations and policy alerts.",
              "To improve the product using aggregated, anonymized usage metrics only.",
            ],
          },
          {
            title: "4a. Employee monitoring disclosure",
            body: [
              "GuardRok monitors employee prompts submitted to AI tools. This constitutes employee monitoring under the laws of many jurisdictions.",
              "The deploying organization is solely responsible for: (a) notifying employees that AI tool usage is monitored; (b) obtaining any required consents; (c) complying with applicable works council, trade union, or consultation requirements.",
              "A sample Employee Monitoring Notice is available on request at hello@guardrok.com.",
            ],
          },
          {
            title: "4b. Data Processing Agreements",
            body: [
              "GuardRok acts as a data processor on behalf of your organization (the data controller).",
              "A Data Processing Agreement (DPA) compliant with GDPR Article 28 is available on request at hello@guardrok.com.",
              "A Business Associate Agreement (BAA) under HIPAA is available for eligible healthcare organizations on request.",
            ],
          },
          {
            title: "5. Data sharing",
            body: [
              "We share data only with trusted infrastructure and service providers necessary to operate the GuardRok platform. These providers are contractually bound to process data only on our instructions.",
              "We do not share data with advertisers, data brokers, or any third party for marketing purposes.",
              "We may disclose data if required to do so by law or valid legal process.",
            ],
          },
          {
            title: "6. Data retention",
            body: [
              "Free plan: event logs retained for 30 days.",
              "Starter plan: 90 days.",
              "Professional plan: 1 year.",
              "Enterprise plan: 7 years (configurable to meet SOX, HIPAA, and other regulatory requirements).",
              "You may request deletion of your organization's data at any time by emailing " + CONTACT + ".",
            ],
          },
          {
            title: "7. Security",
            body: [
              "All data is encrypted in transit and at rest using industry-standard protocols.",
              "Access controls ensure each organization can only access its own data — no cross-tenant access is possible.",
              "GuardRok staff do not have access to your organization's event logs.",
              "We do not store passwords, API keys, or employee credentials.",
            ],
          },
          {
            title: "8. Your rights",
            body: [
              "You may access, correct, or delete your personal data at any time by contacting us.",
              "EU and UK users have rights under GDPR and UK GDPR including data portability and the right to object to processing.",
              "California residents have rights under CCPA including the right to know what data is collected and to opt out of sale. We do not sell data.",
              "To exercise any right, email " + CONTACT + ".",
            ],
          },
          {
            title: "9. Cookies",
            body: [
              "We use only essential session cookies required for authentication. See our Cookie Policy for details.",
            ],
          },
          {
            title: "10. Children",
            body: [
              "GuardRok is a B2B product intended for use by employees aged 18 and over. We do not knowingly collect data from minors.",
            ],
          },
          {
            title: "11. Changes to this policy",
            body: [
              "We will notify organization administrators by email at least 14 days before any material change takes effect.",
            ],
          },
          {
            title: "12. Contact",
            body: [
              "aiApas LLC · hello@guardrok.com · privacy@guardrok.com",
            ],
          },
        ].map(({ title, body }) => (
          <div key={title} style={{ marginBottom: 36 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: "#f1f5f9", marginBottom: 12 }}>{title}</h2>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column" as const, gap: 8 }}>
              {body.map((line, i) => (
                <li key={i} style={{ fontSize: 14, color: "#94a3b8", lineHeight: 1.75, paddingLeft: 16, position: "relative" as const }}>
                  <span style={{ position: "absolute" as const, left: 0, color: "#374151" }}>·</span>
                  {line}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ borderTop: "1px solid #0f172a", padding: "20px 48px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 12, color: "#374151" }}>© 2026 GuardRok™ · aiApas LLC</div>
        <div style={{ display: "flex", gap: 20 }}>
          <Link to="/terms"   style={{ fontSize: 12, color: "#4b5563", textDecoration: "none" }}>Terms</Link>
          <Link to="/cookies" style={{ fontSize: 12, color: "#4b5563", textDecoration: "none" }}>Cookies</Link>
          <a href="mailto:hello@guardrok.com" style={{ fontSize: 12, color: "#4b5563", textDecoration: "none" }}>Contact</a>
        </div>
      </div>
    </div>
  )
}
